import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTodos,
  fetchKategorien,
  fetchVorlagen,
  createTodo,
  updateTodo,
  deleteTodo,
  selectTodos,
  selectKategorien,
  selectTodoStatus,
} from 'modules/todo/features/todoSlice';
import { projectVisited } from 'core/features/projects/projectsSlice';
import { sortTodos, findeKategorie } from 'modules/todo/components/todoConstants';
import BackToOverview from 'core/components/BackToOverview';
import TodoItem from 'modules/todo/components/TodoItem';
import TodoForm from 'modules/todo/components/TodoForm';
import KategorienPanel from 'modules/todo/components/KategorienPanel';
import VorlagenPanel from 'modules/todo/components/VorlagenPanel';

const TABS = [
  { key: 'aufgaben', label: 'Aufgaben' },
  { key: 'vorlagen', label: 'Vorlagen' },
  { key: 'kategorien', label: 'Kategorien' },
];

/**
 * Übersichtsseite des TODO-Moduls mit drei Reitern: Aufgaben, Vorlagen und
 * Kategorien. Lädt beim Öffnen alle Daten und hält den aktiven Reiter lokal.
 */
export default function TodoPage() {
  const dispatch = useDispatch();
  const todos = useSelector(selectTodos);
  const kategorien = useSelector(selectKategorien);
  const status = useSelector(selectTodoStatus);

  const [tab, setTab] = useState('aufgaben');
  const [formOffen, setFormOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState(null);
  const [filterKategorieId, setFilterKategorieId] = useState(null);
  const [zeigeErledigte, setZeigeErledigte] = useState(false);

  useEffect(() => {
    dispatch(projectVisited('todo'));
    dispatch(fetchTodos());
    dispatch(fetchKategorien());
    dispatch(fetchVorlagen());
  }, [dispatch]);

  const sichtbareTodos = useMemo(() => {
    const gefiltert = todos.filter((t) => {
      if (!zeigeErledigte && t.erledigt) return false;
      if (filterKategorieId != null && t.kategorieId !== filterKategorieId) return false;
      return true;
    });
    return sortTodos(gefiltert);
  }, [todos, filterKategorieId, zeigeErledigte]);

  const oeffneNeu = () => {
    setBearbeitet(null);
    setFormOffen(true);
  };

  const oeffneBearbeiten = (todo) => {
    setBearbeitet(todo);
    setFormOffen(true);
  };

  const speichern = (daten) => {
    if (bearbeitet) {
      dispatch(updateTodo({ uuid: bearbeitet.uuid, todo: daten }));
    } else {
      dispatch(createTodo(daten));
    }
    setFormOffen(false);
  };

  const loeschen = (todo) => {
    if (window.confirm(`Aufgabe „${todo.titel}" löschen?`)) {
      dispatch(deleteTodo(todo.uuid));
    }
  };

  const offeneAnzahl = todos.filter((t) => !t.erledigt).length;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>

      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Aufgaben</h1>
        <p className="mt-1 text-sm text-ink-muted">
          {offeneAnzahl} offene {offeneAnzahl === 1 ? 'Aufgabe' : 'Aufgaben'}.
        </p>
      </header>

      <nav className="mb-6 flex gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-brand-600 text-ink'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'aufgaben' && (
        <section>
          <Werkzeugleiste
            kategorien={kategorien}
            filterKategorieId={filterKategorieId}
            onFilter={setFilterKategorieId}
            zeigeErledigte={zeigeErledigte}
            onToggleErledigte={() => setZeigeErledigte((v) => !v)}
            onNeu={oeffneNeu}
          />

          {status === 'loading' && <p className="text-ink-muted">Lade Aufgaben…</p>}

          {status !== 'loading' && sichtbareTodos.length === 0 ? (
            <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
              Keine Aufgaben vorhanden. Lege eine neue Aufgabe an oder erzeuge eine aus einer Vorlage.
            </p>
          ) : (
            <div className="space-y-3">
              {sichtbareTodos.map((todo) => (
                <TodoItem
                  key={todo.uuid}
                  todo={todo}
                  kategorie={findeKategorie(kategorien, todo.kategorieId)}
                  onUpdate={(aktualisiert) => dispatch(updateTodo({ uuid: aktualisiert.uuid, todo: aktualisiert }))}
                  onEdit={oeffneBearbeiten}
                  onDelete={loeschen}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'vorlagen' && <VorlagenPanel onAufgabeErzeugt={() => setTab('aufgaben')} />}

      {tab === 'kategorien' && <KategorienPanel />}

      {formOffen && (
        <TodoForm
          todo={bearbeitet}
          kategorien={kategorien}
          onSubmit={speichern}
          onClose={() => setFormOffen(false)}
        />
      )}
    </div>
  );
}

/** Werkzeugleiste des Aufgaben-Reiters: Neu-Button, Kategoriefilter, Erledigt-Umschalter. */
function Werkzeugleiste({ kategorien, filterKategorieId, onFilter, zeigeErledigte, onToggleErledigte, onNeu }) {
  const chip = (aktiv) =>
    `rounded-full px-3 py-1 text-xs font-medium transition-colors ${
      aktiv ? 'bg-brand-600 text-white' : 'bg-paper-sunken text-ink-muted hover:text-ink'
    }`;

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={onNeu} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500">
          + Neue Aufgabe
        </button>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-ink-muted">
          <input type="checkbox" checked={zeigeErledigte} onChange={onToggleErledigte} className="h-4 w-4 accent-brand-600" />
          Erledigte anzeigen
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onFilter(null)} className={chip(filterKategorieId == null)}>
          Alle
        </button>
        {kategorien.map((k) => (
          <button
            key={k.uuid}
            type="button"
            onClick={() => onFilter(k.id)}
            className={`inline-flex items-center gap-1.5 ${chip(filterKategorieId === k.id)}`}
          >
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: k.farbe }} />
            {k.name}
          </button>
        ))}
      </div>
    </div>
  );
}
